import { isDevelopment as checkIsDevelopment } from './environment'

export enum LogLevel {
  _DEBUG = 0,
  _INFO = 1,
  _WARN = 2,
  _ERROR = 3,
}

export interface ILogContext {
  component?: string
  userId?: string
  sessionId?: string
  chatbotId?: string
  [key: string]: unknown
}

export interface ILogEntry {
  level: LogLevel
  message: string
  value?: unknown
  context?: ILogContext
  timestamp: Date
  error?: Error
}

class Logger {
  private minLevel: LogLevel = LogLevel._WARN
  private isDevelopment: boolean = checkIsDevelopment()

  constructor() {
    if (this.isDevelopment) {
      this.minLevel = LogLevel._DEBUG
    }
  }

  private shouldLog(level: LogLevel): boolean {
    return level >= this.minLevel
  }

  private formatMessage(entry: ILogEntry): string {
    const timestamp = entry.timestamp.toISOString()
    const levelName = LogLevel[entry.level]
    const contextStr = entry.context
      ? ` [${JSON.stringify(entry.context)}]`
      : ''
    const valueStr =
      entry.value !== undefined ? ` VALUE: ${JSON.stringify(entry.value)}` : ''
    return `[${timestamp}] ${levelName}:${contextStr}${valueStr} ${entry.message}`
  }

  private log(
    level: LogLevel,
    message: string,
    value?: unknown,
    context?: ILogContext,
    error?: Error
  ): void {
    if (!this.shouldLog(level)) return

    const entry: ILogEntry = {
      level,
      message,
      value,
      context,
      timestamp: new Date(),
      error,
    }

    if (this.isDevelopment) {
      const formattedMessage = this.formatMessage(entry)

      switch (level) {
        case LogLevel._DEBUG:
          console.debug(formattedMessage, error || '')
          break
        case LogLevel._INFO:
          console.info(formattedMessage, error || '')
          break
        case LogLevel._WARN:
          console.warn(formattedMessage, error || '')
          break
        case LogLevel._ERROR:
          console.error(formattedMessage, error || '')
          break
      }
    }

    this.sendToExternalService(entry)
  }

  debug(message: string, value?: unknown, context?: ILogContext): void {
    this.log(LogLevel._DEBUG, message, value, context)
  }

  info(message: string, value?: unknown, context?: ILogContext): void {
    this.log(LogLevel._INFO, message, value, context)
  }

  warn(
    message: string,
    value?: unknown,
    context?: ILogContext,
    error?: Error
  ): void {
    this.log(LogLevel._WARN, message, value, context, error)
  }

  error(
    message: string,
    value?: unknown,
    context?: ILogContext,
    error?: Error
  ): void {
    this.log(LogLevel._ERROR, message, value, context, error)
  }

  private sendToExternalService(_entry: ILogEntry): void {
    // TODO: Implement Datadog logging service integration
  }

  configureExternalLogging(_config: Record<string, unknown>): void {}
}

// Export singleton instance
export const logger = new Logger()

// Export default for convenience
export default logger
